import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { graphql } from "react-relay";

import cx from "classnames";

import { sawBeacon } from "~/lib/beacons";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import CreateFeedbackButton from "~/nav/panels/Feedback/CreateFeedbackButton";
import FeedbackList from "~/nav/panels/Feedback/FeedbackList";
import NeedsActionBanner from "~/nav/panels/Feedback/NeedsActionBanner";
import LoadingSpinner from "~/ui/LoadingSpinner";

import CategoryDropdown, { FEEDBACK_CATEGORY_VALUES, type FeedbackCategory as FeedbackView } from "./CategoryDropdown";
import Filters, { type TypeValueType } from "./Filters";
import Menu from "./Menu";

import type { FeedbackQuery } from "~/__generated__/FeedbackQuery.graphql";

import panelStyles from "~/ui/CollapsiblePanel/CollapsiblePanel.module.css";

import styles from "./FeedbackPanel.module.css";

const feedbackQuery = graphql`
	query FeedbackQuery {
		viewer {
			account {
				billingAccount {
					hasAccessToFeedback: hasAccessToFeature(feature: feedback)
					hasAccessTo360Feedback: hasAccessToFeature(feature: three_sixty_feedback)
				}
			}
			unreadBeacons
			feedbackActionsAmount
			feedbackUnreadItemsCount
		}
	}
`;

type FeedbackContextType = {
	view: FeedbackView;
	type?: TypeValueType;
	about?: string; // user.id
	from?: string; // user.id
	resetFilters: () => void;
	setView: (v: FeedbackView) => void;
	setType: (v: TypeValueType | undefined) => void;
	setAbout: (v: string | undefined) => void;
	setFrom: (v: string | undefined) => void;
};

export const FeedbackContext = createContext<FeedbackContextType>({
	view: FEEDBACK_CATEGORY_VALUES.INBOX,
	type: undefined,
	about: undefined,
	from: undefined,
	resetFilters: () => {
		throw new Error("Tried to reset filters, but couldn't find FeedbackContext");
	},
	setView: () => {
		throw new Error("Tried to save view, but couldn't find FeedbackContext");
	},
	setType: () => {
		throw new Error("Tried to save type, but couldn't find FeedbackContext");
	},
	setAbout: () => {
		throw new Error("Tried to save about, but couldn't find FeedbackContext");
	},
	setFrom: () => {
		throw new Error("Tried to save from, but couldn't find FeedbackContext");
	},
});

export const FeedbackContextProvider: React.FC<{}> = ({ children }) => {
	const [view, setView] = useState<FeedbackView>(FEEDBACK_CATEGORY_VALUES.INBOX);
	const [type, setType] = useState<TypeValueType | undefined>(undefined);
	const [about, setAbout] = useState<string | undefined>(undefined);
	const [from, setFrom] = useState<string | undefined>(undefined);

	const resetFilters = useCallback(() => {
		setType(undefined);
		setAbout(undefined);
		setFrom(undefined);
	}, []);

	const contextValues = useMemo<FeedbackContextType>(
		() => ({
			view,
			type,
			about,
			from,
			resetFilters,
			setView,
			setType,
			setAbout,
			setFrom,
		}),
		[view, type, about, from, resetFilters],
	);

	return <FeedbackContext.Provider value={contextValues}>{children}</FeedbackContext.Provider>;
};

export default function FeedbackPanel() {
	const { view, type, about, from, setView, setType, setAbout, setFrom } = React.useContext(FeedbackContext);

	const [fetchKey, setFetchKey] = useState<number>(0);

	const props = useLazyLoadQuery<FeedbackQuery>(feedbackQuery, {}, { fetchPolicy: "store-and-network", fetchKey });
	const { viewer } = props;
	const { hasAccessToFeedback, hasAccessTo360Feedback } = viewer.account.billingAccount;
	const showNeedsActionBanner =
		view === FEEDBACK_CATEGORY_VALUES.INBOX && viewer.feedbackActionsAmount > 0 && !type && !about && !from;

	useEffect(() => {
		if (viewer.unreadBeacons.includes("Navbar__feedback")) {
			sawBeacon("Navbar__feedback");
		}
	}, [viewer]);

	const onRefetch = useCallback(() => {
		setFetchKey(n => n + 1);
	}, []);

	const onNeedsActionBannerClick = useCallback(() => {
		setView(FEEDBACK_CATEGORY_VALUES.NEED_ACTION);
	}, [setView]);

	return (
		<section className={cx(styles.panel, panelStyles.panelBase)}>
			<header className={styles.header}>
				<div className={styles.row}>
					<h1 className={styles.heading}>Feedback</h1>
					<div className={styles.headerActions}>
						<CreateFeedbackButton
							hasAccessToFeedback={hasAccessToFeedback}
							hasAccessTo360Feedback={hasAccessTo360Feedback}
						/>
						<Menu unreadCount={viewer.feedbackUnreadItemsCount} />
					</div>
				</div>
				{hasAccessToFeedback && (
					<div className={styles.row}>
						<CategoryDropdown value={view} setView={setView} />
						<React.Suspense fallback={<LoadingSpinner dimmer={false} />}>
							<Filters type={type} about={about} from={from} setType={setType} setAbout={setAbout} setFrom={setFrom} />
						</React.Suspense>
					</div>
				)}
				{showNeedsActionBanner && (
					<NeedsActionBanner amount={viewer.feedbackActionsAmount} onClick={onNeedsActionBannerClick} />
				)}
			</header>
			<React.Suspense fallback={<LoadingSpinner dimmer={false} delay={150} />}>
				<FeedbackList
					onFeedbackListUpdated={onRefetch}
					hasAccessToFeedback={hasAccessToFeedback}
					hasAccessTo360Feedback={hasAccessTo360Feedback}
				/>
			</React.Suspense>
		</section>
	);
}
